import type { RefObject } from 'react';

import { MAX_MESSAGE_LENGTH } from '@/common/constants';
import { HTTP_STATUS } from '@/common/http-status';
import {
  getEffectiveReasoning,
  type DevModelName,
  type DevProvider,
  type DevReasoningLevel,
} from '@/components/providers/dev-tools-provider';
import type { Platform } from '@/components/providers/platform-provider';
import { AnalyticsChatTransport } from '@/lib/assistant-cloud-local/analytics-chat-transport';
import { pushPendingTiming } from '@/lib/assistant-cloud-local/stores/assistant-cloud-store';
import { markServerIdentityEstablished } from '@/lib/assistant-cloud-server';
import {
  clearInitialComposerText,
  getInitialComposerText,
  setInitialComposerText,
} from '@/lib/composer-text-tracking';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import {
  consumeDictationTranscript,
  isDictatedSend,
  setDictationTranscript,
} from '@/lib/dictation/dictation-telemetry';
import { earlyReplayWait } from '@/lib/early-input/handoff';
import { checkSession, noteUnauthorized, settleSignOut } from '@/lib/guest-auth/browser-client';
import { GUEST_SESSION_ENDED } from '@/lib/guest-auth/constants';
import { sessionGeneration, useHistorySession } from '@/lib/history-key/session';
import {
  encodeHitlCredentials,
  HITL_CREDENTIALS_HEADER,
  turnCarriesApprovalResponseFor,
} from '@/lib/hitl/credential-header';
import {
  consumeInterruptionElapsedMs,
  getResumableRecord,
  RESUMABLE_STREAM_SET_EVENT,
} from '@/lib/hitl/resumable-storage';
import type { PiiVerdict } from '@/lib/pii/pii-result-channel';
import {
  maskedMessagesOf,
  PiiRefusalError,
  readPiiRefusal,
  readPiiMask,
  type PiiMasked,
} from '@/lib/pii/server-pii';
import { isUnchecked, markChecked, markUnchecked } from '@/lib/pii/unchecked-messages';
import { siteFor } from '@/lib/site';
import { consumeStarterIdFor, setPendingStarterClick } from '@/lib/starter-click-tracking';
import { useSyncStore } from '@/lib/sync/store';
import { useThreadStatusStore } from '@/lib/thread-status/store';
import { fetchWithTimeout } from '@/lib/utils/api-client';
import { hasConversationStarted, markConversationStarted } from '@/lib/utils/conversation-dedup';
import { getPendingDeletions } from '@/lib/utils/deletion-queue';
import { setServerMessageId } from '@/lib/utils/message';
import { resolveResourceId } from '@/lib/utils/resource-id';
import { safeRandomUUID } from '@/lib/utils/safe-uuid';
import { getThreadUuid } from '@/lib/utils/thread';
import { WebsiteHistoryMismatchError, WEBSITE_HISTORY_MISMATCH } from '@/lib/website-auth/errors';

type TrackEvent = (params: { event: string; properties?: Record<string, unknown> }) => void;

type PostHogClient = { get_distinct_id(): string; captureException(error: Error): void };

interface DevToolsSnapshot {
  showDevTools: boolean;
  selectedModel: DevModelName;
  provider: DevProvider;
  reasoning: DevReasoningLevel;
}

/** Live HITL credential bindings — set by the runtime provider once the tenant module loads. */
export interface HitlTransportBindings {
  getCredentials: () => Promise<Record<string, string>>;
  credentialToolNames: ReadonlySet<string>;
}

export interface CreateChatTransportOptions {
  /** The tenant's agent root (see `chatAgentUrl`). */
  chatAgentUrl: string;
  tenantName: string;
  // Live refs read at request time so the transport sees the latest values
  // without being re-created. Stable values (URL pieces) are captured directly.
  localeRef: { readonly current: string };
  platformRef: { readonly current: Platform | undefined };
  isDebugModeRef: { readonly current: boolean };
  posthogRef: { readonly current: PostHogClient | null | undefined };
  trackEventRef: { readonly current: TrackEvent };
  currentThreadIdRef: RefObject<string | null>;
  erroredThreadIdsRef: RefObject<Set<string>>;
  pendingResponsesRef: RefObject<Map<string, number>>;
  streamingStartedRef: RefObject<Set<string>>;
  retriedResponsesRef: RefObject<Set<string>>;
  lastMessageCountRef: RefObject<Map<string, number>>;
  devToolsRef: { readonly current: DevToolsSnapshot };
  /** Null until the tenant's HITL module (if any) declares getCredentials. */
  hitlRef?: { readonly current: HitlTransportBindings | null };
  /** True when the tenant's hitlPages gate suppressed HITL on this page (forwarded to the backend). */
  hitlSuppressed?: boolean;
  /** True for persistence:'server' tenants — the chat POST then carries only the trailing turn. */
  serverPersistence?: boolean;
  /** Resumable-streaming options for opted-in tenants. */
  resumable?: import('@assistant-ui/react-ai-sdk').AssistantChatResumableOptions;
  /** The server saved the turn with personal data masked; called before the reply streams. */
  onPiiMasked?: (verdict: PiiVerdict<PiiMasked>) => void;
}

type SentTurn = {
  trackingKey: string;
  previousMessageCount: number | undefined;
  threadId: string | undefined;
  /** The guest's message the server judges. */
  messageId: string | undefined;
  /** The server checks every message it is sent, so a success clears them all. */
  sentUserMessageIds: string[];
  /** Reports the guest's new message; held until the server answers, since a turn it blocks as
   *  personal data was never sent. */
  announce: (() => void) | undefined;
  /** Hands back how the message was composed, for the guest's resend of a blocked turn. */
  restoreAttribution: (() => void) | undefined;
};

/** Never leaves the browser: pairs a prepared turn with its own fetch, since two sends can both be
 *  prepared before either is fetched. */
const SENT_TURN_HEADER = 'x-bazak-sent-turn';

/** Taken when the send is prepared: by the time the server answers, the guest may already be
 *  dictating or picking the next message, which these module registries would then describe. */
function takeAttribution(submittedText: string | undefined) {
  const initialComposerText = getInitialComposerText();
  clearInitialComposerText();
  const dictatedTranscript = consumeDictationTranscript();
  const starterId = consumeStarterIdFor(submittedText);
  // Not restored for a resend: that one is made once the chat is ready.
  const earlyWaitedMs = earlyReplayWait.consumeFor(submittedText);
  return {
    initialComposerText,
    dictatedTranscript,
    starterId,
    earlyWaitedMs,
    restore() {
      if (initialComposerText !== null) {
        setInitialComposerText(initialComposerText);
      }
      if (dictatedTranscript) {
        setDictationTranscript(dictatedTranscript);
      }
      if (starterId && submittedText !== undefined) {
        setPendingStarterClick({ starterId, text: submittedText });
      }
    },
  };
}

const RECENT_MESSAGE_COUNT = 10;

/** Reaches back past the recent messages to the oldest one the server has not answered: history
 *  stores it, and every message after it, only once a successful request has carried it. */
function localWindowStart(messages: { id: string; role: string }[]): number {
  const recentStart = Math.max(messages.length - RECENT_MESSAGE_COUNT, 0);
  const oldestUnchecked = messages.findIndex(
    message => message.role === 'user' && isUnchecked(message.id),
  );
  return oldestUnchecked === -1 ? recentStart : Math.min(oldestUnchecked, recentStart);
}

// Builds the chat transport. All mutable inputs flow in as refs so the
// transport reads the latest values inside its option callbacks (which run at
// request time, not during render). Defined in module scope so React-hooks
// lint rules don't analyze ref reads here as "in render".
export function createChatTransport({
  chatAgentUrl,
  tenantName,
  localeRef,
  platformRef,
  isDebugModeRef,
  posthogRef,
  trackEventRef,
  currentThreadIdRef,
  erroredThreadIdsRef,
  pendingResponsesRef,
  streamingStartedRef,
  retriedResponsesRef,
  lastMessageCountRef,
  devToolsRef,
  hitlRef,
  hitlSuppressed,
  serverPersistence,
  resumable,
  onPiiMasked,
}: CreateChatTransportOptions): AnalyticsChatTransport {
  const preparedTurns = new Map<string, SentTurn>();

  // A blocked turn never happened: the guest's resend is a new message rather than a retry, and
  // no response may be reported for it.
  function forgetBlockedTurn({
    trackingKey,
    previousMessageCount,
    threadId,
    restoreAttribution,
  }: SentTurn) {
    restoreAttribution?.();
    if (previousMessageCount === undefined) {
      lastMessageCountRef.current.delete(trackingKey);
    } else {
      lastMessageCountRef.current.set(trackingKey, previousMessageCount);
    }
    if (threadId) {
      pendingResponsesRef.current.delete(threadId);
    }
  }

  // Warms the region list so the first send does not wait on it; a language change reloads the page.
  function reportRegionsError(error: Error) {
    posthogRef.current?.captureException(error);
  }
  void siteFor({ tenant: tenantName, locale: localeRef.current, onError: reportRegionsError });

  const transport: AnalyticsChatTransport = new AnalyticsChatTransport({
    api: `${chatAgentUrl}/chat`,
    resumable,
    credentialed: serverPersistence,
    onStreamStart: (event, properties) => {
      const threadId = (properties as { threadId?: string }).threadId;
      trackEventRef.current({
        event,
        properties: {
          ...properties,
          isRetry: !!threadId && retriedResponsesRef.current.has(threadId),
        },
      });
    },
    onStreamingStarted: threadId => streamingStartedRef.current.add(threadId),
    onStreamFinish: ({ threadId, durationMs, stepTimings }) => {
      // Stash both stream duration and per-step timings; createMessage will
      // consume them when the assistant message is persisted (which happens
      // right after this callback runs). The duration backdates `created_at`,
      // and the step timings populate `step_timings` so the activity-trail can
      // render a per-step breakdown post-reload.
      pushPendingTiming({ threadId, durationMs, timings: stepTimings });
      // Fires on completion, error, AND cancel — the only seam that sees a stream severed by
      // switching away from the thread, where no aui runEnd event is emitted (belt-and-braces
      // next to the runEnd feed; markRunEnd is idempotent). Gated exactly like the feed's writes
      // so non-server-persistence tenants get zero thread-status dots (byte-identical sidebar).
      if (serverPersistence) useThreadStatusStore.getState().markRunEnd(threadId);
    },
    fetch: async (url, options) => {
      const requestUrl = url.toString();
      const isResume = requestUrl.includes('/chat/resume/');
      const { [SENT_TURN_HEADER]: turnKey, ...headers } = Object.fromEntries(
        new Headers(options?.headers),
      );
      const sentTurn = turnKey ? preparedTurns.get(turnKey) : undefined;
      if (turnKey) {
        preparedTurns.delete(turnKey);
      }
      // The UI already shows the guest signed out; a turn must not reach their session meanwhile.
      await settleSignOut(tenantName);
      const sentUnder = sessionGeneration();
      const response = await fetchWithTimeout({
        url: url.toString(),
        options: {
          ...options,
          headers: {
            ...headers,
            Connection: 'keep-alive',
          },
        },
        timeout: 2 * 60 * 1000,
      }).catch((error: unknown) => {
        // Only a server block takes a message back; one that got no answer was still sent, and
        // its resend counts as a retry.
        sentTurn?.announce?.();
        throw error;
      });
      const piiRefusal = await readPiiRefusal(response);
      if (piiRefusal) {
        if (sentTurn) {
          forgetBlockedTurn(sentTurn);
        }
        throw new PiiRefusalError(piiRefusal, sentTurn?.messageId);
      }
      sentTurn?.announce?.();
      const piiMask = isResume ? null : readPiiMask(response);
      if (sentTurn && response.ok) {
        // A masked message is checked once its chat shows the masked text (PiiResultHandler).
        const maskedIds = piiMask
          ? maskedMessagesOf({ result: piiMask, newestMessageId: sentTurn.messageId }).map(
              message => message.id,
            )
          : [];
        markChecked(sentTurn.sentUserMessageIds.filter(id => !maskedIds.includes(id)));
      }
      if (response.status === HTTP_STATUS.UNAUTHORIZED) noteUnauthorized();
      // The guest proxy found the browser's session gone. Once the chat already shows the guest
      // signed out, a cookie left naming a deleted session says nothing new. A session answer since
      // the turn went out (a sign-in, another tab, a resume) may be newer or older than this one,
      // so then only a fresh check decides.
      if (
        response.headers.get(GUEST_SESSION_ENDED.header) === GUEST_SESSION_ENDED.value &&
        useHistorySession.getState().signedIn
      ) {
        if (sessionGeneration() === sentUnder) noteUnauthorized();
        void checkSession(tenantName);
      }
      if (response.status === HTTP_STATUS.FORBIDDEN) {
        const body = await response
          .clone()
          .json()
          .catch(() => null);
        if (body?.error === WEBSITE_HISTORY_MISMATCH) {
          // The website signed in someone else without telling us: drop the stale key, learn who.
          noteUnauthorized();
          void checkSession(tenantName);
          throw new WebsiteHistoryMismatchError();
        }
      }
      if (piiMask) {
        onPiiMasked?.({ result: piiMask, messageId: sentTurn?.messageId });
      }
      // Bodyless statuses (e.g. the resume route's 204 "nothing to replay"): hand back a CLEAN
      // bodyless Response — retry/timeout wrappers can leave a non-null empty body, and the
      // resumable fetch wrapper would then construct `new Response(body, {status: 204})`, which
      // throws. A 204 on the resume GET also means the stream is gone — drop the stored record
      // so later boots stop re-asking.
      if (response.status === 204 || response.status === 205 || response.status === 304) {
        if (resumable && isResume) {
          resumable.storage.clear();
        }
        return new Response(null, { status: response.status, headers: response.headers });
      }
      // A resume GET that returns bytes (200) is a real re-attach — fire RESPONSE_RESUMED (the
      // resume half of the interrupt→resume funnel, parallel to RESPONSE_INTERRUPTED). Gated to
      // resumable tenants by construction (`resumable` is set only for them).
      if (resumable && response.ok && isResume) {
        const record = getResumableRecord(tenantName);
        const threadId = record?.threadUuid;
        const msSinceInterruption = threadId
          ? consumeInterruptionElapsedMs({ tenantName, threadUuid: threadId })
          : undefined;
        trackEventRef.current({
          event: CHAT_ANALYTICS_EVENTS.RESPONSE_RESUMED,
          properties: {
            threadId,
            via: record?.source ?? 'header',
            ...(msSinceInterruption !== undefined && { msSinceInterruption }),
          },
        });
      }
      const messageId = response.headers.get('X-Message-Id');
      if (currentThreadIdRef.current && messageId) {
        setServerMessageId(currentThreadIdRef.current, messageId);
      }
      // A successful chat POST minted (or refreshed) the HttpOnly identity cookie — record the
      // client-readable marker that authorizes AssistantCloudServer to start reading history.
      // Only server-persistence tenants mint the cookie / read via that marker; skip the write
      // for everyone else so no stray localStorage marker appears for non-opted tenants.
      if (serverPersistence && response.ok && options?.method === 'POST') {
        markServerIdentityEstablished({ tenant: tenantName });
      }
      return response;
    },
    prepareSendMessagesRequest: async params => {
      const distinctId = resolveResourceId(posthogRef.current?.get_distinct_id());
      const threadId = params.id ? getThreadUuid(params.id) : undefined;
      const deletedMessageIds = params.id ? await getPendingDeletions(params.id) : [];

      currentThreadIdRef.current = threadId ?? null;

      // Resumable tenants: point the persisted current-thread at the thread with the in-flight
      // run as soon as it starts (thread restore otherwise only learns about it at run END, so a
      // mid-stream reload would boot into a blank thread with nothing to resume).
      // Mint the resumable stream id client-side and send it as a request header so the backend uses
      // it (and echoes it back). Announce it to sibling tabs NOW (at send): the receiver doesn't
      // remount immediately — it polls the discovery endpoint until the thread exists in the DB
      // (which the backend persists before the first token) and only then adopts it. This lets a
      // sibling show the thread + question as soon as the run is created, without waiting for the
      // first token (important for tool-calling turns).
      let clientStreamId: string | undefined;
      if (resumable && threadId) {
        useSyncStore.getState().setThreadId(threadId);
        clientStreamId = safeRandomUUID();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(RESUMABLE_STREAM_SET_EVENT, {
              detail: { streamId: clientStreamId, threadUuid: threadId },
            }),
          );
        }
      }

      const userMessages = params.messages.filter(m => m.role === 'user');
      const messageCount = userMessages.length;
      const isFirstMessage = messageCount === 1;

      // Use params.id (raw thread ID) as the key for tracking, since threadId (UUID) may change
      // between first message and retry when local IDs are involved
      const trackingKey = params.id || 'new-thread';

      const previousMessageCount = lastMessageCountRef.current.get(trackingKey);
      // Answering an approval or a human tool's card sends the assistant's own turn back to finish:
      // no new message from the guest, and no retry of one. Told apart by the request itself, since
      // the count above starts empty after a reload or a thread switch.
      const continuesAssistantTurn = params.messages.at(-1)?.role === 'assistant';
      const isRetry =
        !continuesAssistantTurn &&
        previousMessageCount !== undefined &&
        messageCount === previousMessageCount;

      lastMessageCountRef.current.set(trackingKey, messageCount);

      if (threadId && erroredThreadIdsRef.current.has(threadId)) {
        erroredThreadIdsRef.current.delete(threadId);
      }

      // Only fire user_message_sent for NEW messages, not retries
      // Retries are tracked via response_retry_clicked or response_auto_retried
      const isNewMessage = !isRetry && !continuesAssistantTurn;
      const lastUserMessage = userMessages[userMessages.length - 1];
      const submittedText = lastUserMessage?.parts?.find(
        (p): p is { type: 'text'; text: string } => p.type === 'text',
      )?.text;
      const attribution = isNewMessage ? takeAttribution(submittedText) : undefined;
      function announce({
        initialComposerText,
        dictatedTranscript,
        starterId,
        earlyWaitedMs,
      }: ReturnType<typeof takeAttribution>) {
        if (isFirstMessage && (!threadId || !hasConversationStarted(threadId))) {
          trackEventRef.current({
            event: CHAT_ANALYTICS_EVENTS.CONVERSATION_STARTED,
            properties: { threadId },
          });
          if (threadId) {
            markConversationStarted(threadId);
          }
        }

        const dictated = isDictatedSend({ submittedText, transcript: dictatedTranscript });

        const userMessageProperties = {
          threadId,
          messageCount,
          ...(starterId && { starterId }),
          ...(earlyWaitedMs !== undefined && { beforeReady: true, waitedMs: earlyWaitedMs }),
          ...(initialComposerText && { submittedAsIs: initialComposerText === submittedText }),
          ...(dictated && { dictated: true, dictatedChars: dictatedTranscript.length }),
        };
        trackEventRef.current({
          event: CHAT_ANALYTICS_EVENTS.USER_MESSAGE_SENT,
          properties: userMessageProperties,
        });
      }
      // Server-persistence tenants: Mastra memory is the history — the docs warn that sending
      // prior messages alongside memory causes ordering/duplicate bugs. Send only the trailing
      // turn (last user message onward, which keeps HITL resolved-tool / approval-response
      // assistant parts that follow it); other tenants keep the last-10 window.
      const lastUserIndex = serverPersistence
        ? params.messages.findLastIndex(m => m.role === 'user')
        : -1;
      const turnMessages =
        serverPersistence && lastUserIndex >= 0
          ? params.messages.slice(lastUserIndex)
          : params.messages.slice(localWindowStart(params.messages));
      // Server-persistence tenants send only the newest turn, so the server never re-checks an
      // earlier message; none is held back for them.
      if (!continuesAssistantTurn && lastUserMessage && !serverPersistence) {
        markUnchecked(lastUserMessage.id);
      }
      const turnKey = safeRandomUUID();
      preparedTurns.set(turnKey, {
        trackingKey,
        previousMessageCount,
        threadId,
        messageId: userMessages.at(-1)?.id,
        sentUserMessageIds: turnMessages.filter(m => m.role === 'user').map(m => m.id),
        announce: attribution && (() => announce(attribution)),
        restoreAttribution: attribution?.restore,
      });

      const site = await siteFor({
        tenant: tenantName,
        locale: localeRef.current,
        onError: reportRegionsError,
      });

      const requestStartTime = Date.now();
      if (threadId) {
        pendingResponsesRef.current.set(threadId, requestStartTime);
        // Read by the lifecycle events; cleared on non-retry so a previous
        // run's flag can't leak onto this response.
        if (isRetry) retriedResponsesRef.current.add(threadId);
        else retriedResponsesRef.current.delete(threadId);
      }
      transport.setRequestContext(threadId ?? null, requestStartTime);

      const recentMessages = turnMessages.map(message => {
        if (message.role !== 'user') return message;
        return {
          ...message,
          parts: message.parts.map(part =>
            part.type === 'text' ? { ...part, text: part.text.slice(0, MAX_MESSAGE_LENGTH) } : part,
          ),
        };
      });

      const platform = platformRef.current;
      const isDebugMode = isDebugModeRef.current;

      // Server-approval credentials: harvested fresh and attached as a header ONLY on the turn that
      // carries the approval response for a credential-declaring tool. Never in the body.
      let credentialHeaders: Record<string, string> | undefined;
      const hitl = hitlRef?.current;
      if (
        hitl &&
        turnCarriesApprovalResponseFor({
          messages: params.messages,
          toolNames: hitl.credentialToolNames,
        })
      ) {
        const credentials = await hitl.getCredentials();
        if (Object.keys(credentials).length > 0) {
          credentialHeaders = { [HITL_CREDENTIALS_HEADER]: encodeHitlCredentials(credentials) };
        }
      }

      return {
        ...params,
        headers: {
          ...params.headers,
          ...credentialHeaders,
          ...(clientStreamId && { 'x-resumable-stream-id': clientStreamId }),
          [SENT_TURN_HEADER]: turnKey,
        },
        body: {
          memory: {
            thread: threadId,
            resource: distinctId,
          },
          messages: recentMessages,
          deletedMessageIds,
          languageCode: localeRef.current,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(platform && { platform }),
          ...(site && { site }),
          ...(hitlSuppressed && { hitlSuppressed: true }),
          ...(isDebugMode && { debug: isDebugMode }),
          ...(devToolsRef.current.showDevTools && {
            reasoning: getEffectiveReasoning({
              model: devToolsRef.current.selectedModel,
              provider: devToolsRef.current.provider,
              reasoning: devToolsRef.current.reasoning,
            }),
            model: devToolsRef.current.selectedModel,
            provider: devToolsRef.current.provider,
          }),
        },
      };
    },
  });

  return transport;
}
